import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, EMPTY, firstValueFrom, map, tap } from 'rxjs';
import { IProfileScoreResponse } from '@core/interface';
import { API, environment } from '@core/envs';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
/**
 * Service responsável por gerenciar os dados do score do perfil do usuário.
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileScoreService {
  readonly #http = inject(HttpClient);
  private readonly scoreProfile = signal<IProfileScoreResponse | null>(null);
  readonly scoreProfileComputed = computed(() => this.scoreProfile());

  readonly error = signal<boolean>(false);
  readonly isLoading = signal<boolean>(false);

  /**
   * Obtém o score do cliente.
   *
   * Endpoint: GET /customers/score?country=BRA&region=SA
   *
   * @param country - Código do país (ex: 'BRA')
   * @optional @param region - Código da região (ex: 'SA')
   */
  async getCustomerScore(country: string, region = 'SA') {
    const api = environment.getRoute(API.users);
    const url = `${api}/customers/score?country=${country}&region=${region}`;
    this.isLoading.set(true);

    try {
      await firstValueFrom(
        this.#http
          .get<IProfileScoreResponse>(url, { observe: 'response' })
          .pipe(
            map((response) => {
              if (response.status === HttpStatusCode.NoContent) {
                this.#handleErrorOrNoContent();
              }
              return response.body;
            }),
            tap((data) => {
              this.scoreProfile.set(data);
              this.isLoading.set(false);
              this.error.set(false);
            }),
            catchError(() => {
              this.#handleErrorOrNoContent();
              return EMPTY;
            })
          )
      );
    } catch (error) {
      this.#handleErrorOrNoContent();
    }
  }

  #handleErrorOrNoContent() {
    this.scoreProfile.set(null);
    this.isLoading.set(false);
    this.error.set(true);
  }
}
